import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ReferentielParameter, NavigationState } from '~/types/referentiel'
import { parseMultipleCodes } from '~/utils/text'

/**
 * Store pour la gestion des URLs de navigation et normalisation
 *
 * CENTRALISATION :
 * - URLs de navigation et routing
 * - IDs HTML et éléments DOM (ex-seo.ts)
 * - Toute la logique de normalisation de texte
 */

// Types pour la navigation URL
interface UrlSegment {
  type: 'category' | 'collection'
  codes: string[]
  originalString: string
}

export const useUrlNavigationStore = defineStore('urlNavigation', () => {

  // État pour sauvegarder la dernière recherche
  const lastSearchQuery = ref('')

  const generateParameterSlug = (parameter: any): string => {
    if (!parameter) return ''
    if (parameter.slug) return parameter.slug

    // Pour les sous-catégories, utiliser le préfixe 'sous-cat-'
    let prefix = 'cat-'
    if (parameter.sub_type === 'sous_categorie') {
      prefix = 'sous-cat-'
    } else if (parameter.key?.includes('col_')) {
      prefix = 'col-'
    }

    // Handle both ParameterItem and Category object structures
    const labelObject = parameter.label || parameter;
    const parameterName = labelObject.fr || labelObject.en || parameter.fr || parameter.en || parameter.code || parameter.category_code || ''

    const normalizedName = normalizeCodeForUrl(parameterName)
    return prefix + normalizedName
  }

  const findParameterBySlug = (
    slug: string,
    parameters: any[],
    catalogue: string
  ): ReferentielParameter | null => {
    if (!slug || !parameters.length) return null

    const isCollection = slug.startsWith('col-')
    const isCategory = slug.startsWith('cat-')

    if (!isCollection && !isCategory) return null

    const keyToSearch = isCollection
      ? (catalogue === 'mobilier' ? 'ref_col_mobilier' : catalogue === 'orfevrerie' ? 'ref_col_orfevrerie' : 'ref_col_porcelaine')
      : (catalogue === 'mobilier' ? 'ref_cat_mobilier' : catalogue === 'orfevrerie' ? 'ref_cat_orfevrerie' : 'ref_cat_porcelaine');

    return parameters.find((param: any) => {
      if (param.key !== keyToSearch) return false

      const paramSlug = generateParameterSlug(param)
      return paramSlug === slug
    }) || null
  }

  const buildNavigationState = (
    parameter: any,
    catalogue: string
  ): NavigationState => {
    const itemType = parameter.key?.includes('col_') ? 'collection' : 'category'
    const slug = generateParameterSlug(parameter)

    // Pour le filtrage :
    // - Catégories : utiliser le category_code (qui peut contenir plusieurs catégories)
    // - Collections : utiliser le category_code
    const filteringValue = parameter.category_code || parameter.fr || parameter.en || ''

    return {
      itemType,
      categoryCode: filteringValue,
      categoryLabel: parameter.fr || parameter.en || parameter.category_code,
      currentSlug: slug,
      catalogue
    }
  }

  const generateParameterUrl = (parameter: any, catalogue: string): string => {
    const slug = generateParameterSlug(parameter)
    return `/products/${catalogue}/${slug}`
  }

  const generateMaterialUrl = (catalogue: string, categorySlug: string, modelCode: string): string => {
    const normalizedModelCode = normalizeCodeForUrl(modelCode)
    return `/products/${catalogue}/${categorySlug}/${normalizedModelCode}`
  }

  const generateProductUrl = (catalogue: string, categorySlug: string, modelCode: string, productReference: string): string => {
    const normalizedModelCode = normalizeCodeForUrl(modelCode)
    const normalizedReference = productReference.replace(/\s+/g, '_')
    return `/products/${catalogue}/${categorySlug}/${normalizedModelCode}/${normalizedReference}`
  }

  const generateBackUrlFromMaterial = (
    parentSlug: string,
    catalogue: string
  ): string => {
    return `/products/${catalogue}/${parentSlug}`
  }

  const generateBackUrlFromProduct = (
    parentSlug: string,
    modelCode: string,
    catalogue: string
  ): string => {
    return `/products/${catalogue}/${parentSlug}/${normalizeCodeForUrl(modelCode)}`
  }

  const generateSmartBackUrlFromProduct = (
    parentSlug: string,
    modelCode: string,
    catalogue: string,
    hasMultipleMaterials: boolean = false
  ): string => {
    if (!modelCode) {
      // Si pas de modèle, retour vers catégorie
      return `/products/${catalogue}/${parentSlug}`
    }

    if (hasMultipleMaterials) {
      // Plusieurs déclinaisons → retour vers page matière
      return `/products/${catalogue}/${parentSlug}/${normalizeCodeForUrl(modelCode)}`
    } else {
      // Une seule déclinaison → retour vers catégorie
      return `/products/${catalogue}/${parentSlug}`
    }
  }

  const findParentParameterForProduct = (
    product: any,
    parameters: any[],
    catalogue: string
  ): ReferentielParameter | null => {
    if (!product.segmentationCatalogue || !parameters.length) return null;

    const categoryKey = catalogue === 'mobilier' ? 'ref_cat_mobilier' :
                        catalogue === 'orfevrerie' ? 'ref_cat_orfevrerie' : 'ref_cat_porcelaine';

    let parentParam = parameters.find((param: any) =>
      param.key === categoryKey && param.category_code === product.segmentationCatalogue
    );

    if (parentParam) return parentParam;

    const collectionKey = catalogue === 'mobilier' ? 'ref_col_mobilier' :
                          catalogue === 'orfevrerie' ? 'ref_col_orfevrerie' : 'ref_col_porcelaine';

    parentParam = parameters.find((param: any) =>
      param.key === collectionKey && param.category_code === product.segmentationCatalogue
    );

    return parentParam || null;
  }

  /**
   * Normalise un code pour les URLs de navigation
   * Exemple: "Bureaux et consoles" -> "bureaux-et-consoles"
   *
   * Utilisé pour :
   * - URLs de navigation (/products/cat-bureaux-et-consoles)
   * - IDs HTML (#material-bureau-console)
   */
  const normalizeCodeForUrl = (code: string): string => {
    if (!code) return ''

    return code
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[ñ]/g, 'n')
      .replace(/[ÿ]/g, 'y')
      .replace(/[ÀÁÂÃÄÅ]/g, 'A')
      .replace(/[ÈÉÊË]/g, 'E')
      .replace(/[ÌÍÎÏ]/g, 'I')
      .replace(/[ÒÓÔÕÖ]/g, 'O')
      .replace(/[ÙÚÛÜ]/g, 'U')
      .replace(/[Ç]/g, 'C')
      .replace(/[Ñ]/g, 'N')
      .replace(/[Ÿ]/g, 'Y')
      .toLowerCase()
  }

  const denormalizeCodeFromUrl = (urlCode: string): string => {
    if (!urlCode) return ''

    return urlCode.replace(/-/g, ' ')
  }

  // parseMultipleCodes imported from '~/utils/text'

  // Fonctions pour gérer la recherche
  const saveSearchQuery = (query: string) => {
    lastSearchQuery.value = query
  }

  const getLastSearchQuery = () => {
    return lastSearchQuery.value
  }

  const clearSearchQuery = () => {
    lastSearchQuery.value = ''
  }

  return {
    generateParameterSlug,
    findParameterBySlug,
    buildNavigationState,
    generateParameterUrl,
    generateMaterialUrl,
    generateProductUrl,
    generateBackUrlFromMaterial,
    generateBackUrlFromProduct,
    generateSmartBackUrlFromProduct,
    findParentParameterForProduct,
    parseMultipleCodes,
    normalizeCodeForUrl,
    denormalizeCodeFromUrl,
    // Alias pour compatibilité avec l'ancien seo.ts
    normalizeForUrl: normalizeCodeForUrl,
    // Fonctions de recherche
    lastSearchQuery,
    saveSearchQuery,
    getLastSearchQuery,
    clearSearchQuery,
  }
})
